<template>
    <div v-if="modelValue || displayAsForm" class="m-3">
        <div v-if="!displayAsForm" class="modal-backdrop animated-backdrop"></div>
        <div :class="displayAsForm ? 'form-inline card' : 'modal-dialog-centered modal-animated'">
            <div class="modal-content">
                <div :class="displayAsForm ? 'form-header card-header' : 'modal-header bg-gradient-primary'">
                    <h5 :class="displayAsForm ? 'form-title' : 'modal-title text-white'">{{ localIsEditMode ? 'Editar' :
                        'Crear' }}</h5>
                    <button v-if="!displayAsForm" type="button" class="btn-close" @click="closeModal"></button>
                </div>
                <form @submit.prevent="submitForm">
                    <div class="modal-body card-body">
                        <slot :entity="entity" :errors="errors"></slot>
                    </div>
                    <div class="modal-footer card-footer">
                        <argon-button v-if="!displayAsForm" color="light" variant="gradient" @click="closeModal">
                            Cancelar
                        </argon-button>
                        <argon-button color="primary" variant="gradient" type="submit">
                            {{ localIsEditMode ? 'Actualizar' : 'Crear' }}
                        </argon-button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, getCurrentInstance, computed, nextTick } from 'vue';
import { useStore } from 'vuex';
import ArgonButton from "@/components/ArgonButton.vue";
import '@/assets/css/modalstore.css';

const props = defineProps({
    modelValue: Boolean,
    entityData: {
        type: Object,
        default: () => ({}),
    },
    isEditMode: {
        type: Boolean,
        default: false,
    },
    resourceApi: {
        type: String,
        required: true
    },
    displayAsForm: {
        type: Boolean,
        default: false
    },
    keepFormOpen: {
        type: Boolean,
        default: false
    },
    resourceName: {
        type: String,
        default: ''
    }
});

const emit = defineEmits(['update:modelValue', 'entityUpdated']);
const entity = ref({ ...props.entityData });
const errors = ref({});
const globalError = ref(null);
const localIsEditMode = ref(props.isEditMode);
const store = useStore();

watch(() => props.modelValue, (newValue) => {
    if (newValue) {
        entity.value = { ...props.entityData };
        errors.value = {};
        globalError.value = null;
    }
});

watch(() => props.entityData, (newData) => {
    entity.value = { ...newData };
});

watch(() => props.isEditMode, (newMode) => {
    localIsEditMode.value = newMode;
});

watch(() => store.getters['modelStore/fieldErrors'], (newErrors) => {
    errors.value = newErrors;
});

const { proxy } = getCurrentInstance();

const submitForm = async () => {
    const actionType = localIsEditMode.value ? "editModel" : "addModel";

    await nextTick();

    // Normalizar los datos: convertir objetos {id, label} a solo el ID
    const normalizedData = { ...entity.value };
    Object.keys(normalizedData).forEach(key => {
        // Si es un objeto con estructura {id, label}, extraer solo el id
        if (normalizedData[key] && typeof normalizedData[key] === 'object' && 'id' in normalizedData[key]) {
            normalizedData[key] = normalizedData[key].id;
        }
    });

    try {
        const response = await proxy.$store.dispatch(`modelStore/${actionType}`, {
            resource: props.resourceApi,
            itemData: normalizedData
        });
        if (response.data && (response.data.code_number === 200 || response.data.code_number === 201)) {
            emit('entityUpdated', response.data.data);
            const successMessage = localIsEditMode.value ? `${props.resourceName} actualizado correctamente` : `${props.resourceName} creado correctamente`;
            proxy.$notify(successMessage, '', 'success', 'ni ni-like-2 ni-lg');
            if (!props.displayAsForm || !props.keepFormOpen) {
                closeModal();
            } else {
                entity.value = response.data.data;
                localIsEditMode.value = true;
            }
        }
    } catch (error) {
        if (error.response) {
            if (error.response.status === 422) {
                errors.value = error.response.data.errors || {};
                const numberOfErrors = computed(() => Object.keys(errors.value).length);
                proxy.$notify(
                    `Error al guardar ${props.resourceName}`,
                    `Revisar ${numberOfErrors.value} ${numberOfErrors.value === 1 ? 'error' : 'errores'} en el formulario`,
                    'danger',
                    'ni ni-fat-remove ni-lg'
                );
            } else if (error.response.status === 403) {
                globalError.value = error.response.data.message === "This action is unauthorized." ? "No tienes permiso para realizar esta acción." : error.response.data.message;
                proxy.$notify(
                    `Error al guardar ${props.resourceName}`,
                    globalError.value,
                    'danger',
                    'ni ni-fat-remove ni-lg'
                );
            } else if (error.response.status === 409) {
                globalError.value = error.response.data.message === "This action is unauthorized." ? "No tienes permiso para realizar esta acción." : error.response.data.message;
                proxy.$notify(
                    `Error al guardar ${props.resourceName}`,
                    globalError.value,
                    'danger',
                    'ni ni-fat-remove ni-lg'
                );
            } else {
                globalError.value = error.message;
                proxy.$notify(
                    `Error al guardar ${props.resourceName}`,
                    globalError.value,
                    'danger',
                    'ni ni-fat-remove ni-lg'
                );
            }
        } else {
            globalError.value = error.message;
            proxy.$notify(
                `Error al guardar ${props.resourceName}`,
                globalError.value,
                'danger',
                'ni ni-fat-remove ni-lg'
            );
        }
    }
};

const closeModal = () => {
    emit('update:modelValue', false);
};
</script>

<style scoped>
.form-inline {
    margin: 20px 0;
}

.form-header {
    background-color: #f8f9fa;
    padding: 10px;
    border-bottom: 1px solid #dee2e6;
}

.form-title {
    margin: 0;
    font-size: 1.25rem;
}

.card-header {
    background-color: #f8f9fa;
    padding: 10px;
    border-bottom: 1px solid #dee2e6;
}

.card-body {
    padding: 20px;
}

.card-footer {
    padding: 10px;
    border-top: 1px solid #dee2e6;
    display: flex;
    justify-content: flex-end;
}
</style>
